import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { appointmentApiService, AdminAppointment } from '../services/appointmentApi';
import { Calendar, Clock, User, Mail, Phone, MessageSquare, Edit, Save, X } from 'lucide-react';

const AppointmentsList: React.FC = () => {
  const [appointments, setAppointments] = useState<AdminAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await appointmentApiService.getAllAppointments();
      setAppointments(response.appointments);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditStart = (appointment: AdminAppointment) => {
    setEditingId(appointment.id);
    setEditStatus(appointment.status);
    setEditNotes(appointment.internal_notes || '');
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditStatus('');
    setEditNotes('');
  };

  const handleEditSave = async (appointmentId: number) => {
    try {
      const updatedAppointment = await appointmentApiService.updateAppointmentStatus(
        appointmentId,
        editStatus,
        editNotes
      );
      
      // Mettre à jour la liste
      setAppointments(prev => 
        prev.map(app => app.id === appointmentId ? updatedAppointment : app)
      );
      
      setEditingId(null);
      setEditStatus('');
      setEditNotes('');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const filteredAppointments = appointments.filter(appointment => {
    if (filterStatus === 'all') return true;
    return appointment.status === filterStatus;
  });

  const getStatusOptions = () => [
    { value: 'pending', label: 'En attente' },
    { value: 'confirmed', label: 'Confirmé' },
    { value: 'cancelled', label: 'Annulé' },
    { value: 'completed', label: 'Terminé' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-2">
          <span className="text-red-600 text-xl">❌</span>
          <div>
            <h3 className="text-red-800 font-semibold">Erreur</h3>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
        <button
          onClick={loadAppointments}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">📅 Rendez-vous pris</h2>
          <p className="text-gray-600 mt-1">
            {appointments.length} rendez-vous au total
          </p>
        </div>
        <button
          onClick={loadAppointments}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          🔄 Actualiser
        </button>
      </div>

      {/* Filtres */}
      <div className="flex items-center space-x-4">
        <span className="text-sm font-medium text-gray-700">Filtrer par statut:</span>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">Tous les statuts</option>
          {getStatusOptions().map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="text-sm text-gray-500">
          {filteredAppointments.length} résultat(s)
        </span>
      </div>

      {/* Liste des rendez-vous */}
      {filteredAppointments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun rendez-vous</h3>
          <p className="text-gray-600">
            {filterStatus === 'all' 
              ? "Aucun rendez-vous n'a encore été pris"
              : `Aucun rendez-vous avec le statut "${getStatusOptions().find(o => o.value === filterStatus)?.label}"`
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {filteredAppointments.map((appointment) => (
              <motion.div
                key={appointment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    {/* Informations principales */}
                    <div className="flex-1 space-y-4">
                      {/* Client et contact */}
                      <div className="flex items-start space-x-6">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <User className="w-5 h-5 text-gray-400" />
                            <h3 className="text-lg font-semibold text-gray-900">
                              {appointment.client_full_name}
                            </h3>
                          </div>
                          <div className="space-y-1">
                            {appointment.client_email && (
                              <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <Mail className="w-4 h-4" />
                                <span>{appointment.client_email}</span>
                              </div>
                            )}
                            {appointment.client_phone && (
                              <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <Phone className="w-4 h-4" />
                                <span>{appointment.client_phone}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Date et heure */}
                        <div className="text-right">
                          <div className="flex items-center space-x-2 mb-1">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900">
                              {appointment.appointment_date && 
                                new Date(appointment.appointment_date).toLocaleDateString('fr-FR', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })
                              }
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {appointment.appointment_time}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Sujet et message */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">
                          📋 {appointment.subject}
                        </h4>
                        {appointment.message && (
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-start space-x-2">
                              <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5" />
                              <p className="text-sm text-gray-600">{appointment.message}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Type et statut */}
                      <div className="flex items-center space-x-4">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                          {appointment.appointment_type === 'online' ? '💻 En ligne' : '🏢 Physique'}
                        </span>
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${appointmentApiService.getStatusColor(appointment.status)}`}>
                          {appointment.status_display}
                        </span>
                        <span className="text-xs text-gray-500">
                          Créé le {new Date(appointment.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="ml-6">
                      {editingId === appointment.id ? (
                        <div className="space-y-3">
                          <select
                            value={editStatus}
                            onChange={(e) => setEditStatus(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          >
                            {getStatusOptions().map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <textarea
                            value={editNotes}
                            onChange={(e) => setEditNotes(e.target.value)}
                            placeholder="Notes internes..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            rows={2}
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditSave(appointment.id)}
                              className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                            >
                              <Save className="w-4 h-4" />
                              <span>Sauver</span>
                            </button>
                            <button
                              onClick={handleEditCancel}
                              className="flex items-center space-x-1 px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                            >
                              <X className="w-4 h-4" />
                              <span>Annuler</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEditStart(appointment)}
                          className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          <Edit className="w-4 h-4" />
                          <span>Modifier</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Notes internes */}
                  {appointment.internal_notes && editingId !== appointment.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <h5 className="text-sm font-medium text-yellow-800 mb-1">📝 Notes internes</h5>
                        <p className="text-sm text-yellow-700">{appointment.internal_notes}</p>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default AppointmentsList;
